// Categories — Supabase implementation.

import { supabase } from '@/lib/supabase';
import { mapCategory } from './mappers';
import type { Category } from '@/types/domain';

export interface ListCategoriesOptions { isActive?: boolean }

export async function listCategories(opts: ListCategoriesOptions = {}): Promise<Category[]> {
  let q = supabase.from('categories').select('*').order('sort_order').order('name');
  if (opts.isActive !== undefined) q = q.eq('is_active', opts.isActive);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []).map(mapCategory);
}

export async function getCategory(id: string): Promise<Category | null> {
  const { data, error } = await supabase
    .from('categories').select('*').eq('id', id).maybeSingle();
  if (error) throw new Error(error.message);
  return data ? mapCategory(data) : null;
}

export interface CreateCategoryInput {
  name: string;
  description?: string | null;
  sortOrder?: number;
  /** Accepted but ignored — the actor comes from auth.uid() server-side. */
  createdBy?: string;
}

/** Returns the new category's id. */
export async function createCategory(input: CreateCategoryInput): Promise<string> {
  const { data, error } = await supabase
    .from('categories')
    .insert({
      name: input.name.trim(),
      description: input.description || null,
      sort_order: input.sortOrder ?? 0,
    })
    .select('id')
    .single();
  if (error) {
    // Unique on lower(name) — categories are matched case-insensitively.
    if (error.code === '23505') throw new Error(`Kategori "${input.name}" sudah ada`);
    throw new Error(error.message);
  }
  return data.id;
}

export type UpdateCategoryInput = Partial<CreateCategoryInput>;

export async function updateCategory(id: string, input: UpdateCategoryInput): Promise<void> {
  const patch: Record<string, unknown> = {};
  if (input.name !== undefined) patch.name = input.name.trim();
  if (input.description !== undefined) patch.description = input.description || null;
  if (input.sortOrder !== undefined) patch.sort_order = input.sortOrder;
  const { error } = await supabase.from('categories').update(patch as never).eq('id', id);
  if (error) {
    if (error.code === '23505') throw new Error(`Kategori "${input.name}" sudah ada`);
    throw new Error(error.message);
  }
}

export async function setCategoryActive(id: string, isActive: boolean): Promise<void> {
  const { error } = await supabase.from('categories').update({ is_active: isActive }).eq('id', id);
  if (error) throw new Error(error.message);
}

/** Soft delete, so products filed under it keep their grouping. */
export const deleteCategory = (idOrCategory: string | { id: string }) =>
  setCategoryActive(
    typeof idOrCategory === 'string' ? idOrCategory : idOrCategory.id,
    false,
  );

/**
 * Hard delete. Products are NOT deleted with it — the FK is ON DELETE SET
 * NULL, so they simply become uncategorised.
 */
/**
 * Hard delete. `productCount` is a caller-side guard so the UI can refuse to
 * orphan a large catalog; the FK is ON DELETE SET NULL, so products survive
 * either way and simply become uncategorised.
 */
export async function hardDeleteCategory(id: string, productCount = 0): Promise<void> {
  if (productCount > 0) {
    throw new Error(`Kategori masih dipakai ${productCount} produk`);
  }
  const { error } = await supabase.from('categories').delete().eq('id', id);
  if (error) throw new Error(error.message);
}
